import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../hooks";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../../constants/enums";
import {
  downloadCommAutomobileAuthorizationPDFAsync,
  downloadCommAutomobileConsentFormPDFAsync,
  downloadCommAutomobileLiabilityInsurancePDFAsync,
  downloadCommAutomobileOcep16PDFAsync,
  downloadCommAutomobileOcep17PDFAsync,
  downloadCommAutomobileOcep28AListPDFAsync,
  downloadCommAutomobileRetireeDiscountPDFAsync,
  downloadCommAutomobileSharingFillablePDFAsync,
  fetchCommAutoPolicyFormsListAsync,
  previewCommAutomobileAuthorizationPDFAsync,
  previewCommAutomobileConsentFormPDFAsync,
  previewCommAutomobileLiabilityInsurancePDFAsync,
  previewCommAutomobileOcep16PDFAsync,
  previewCommAutomobileOcep17PDFAsync,
  previewCommAutomobileOcep28AListPDFAsync,
  previewCommAutomobileRetireeDiscountPDFAsync,
  previewCommAutomobileSharingFillablePDFAsync,
} from "../../../../../../redux/commAutoPolicy/commAutoPolicyForms/commAutoPolicyFormsActions";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import {
  alpha,
  Button,
  Menu,
  MenuItem,
  MenuProps,
  Stack,
  styled,
} from "@mui/material";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { ICommAutoPolicyForm } from "../../../../../../redux/commAutoPolicy/commAutoPolicyForms/commAutoPolicyForm.types";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatText } from "../../../../../../helpers";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import { useDataTableV2Pagination } from "../../../../../../components/TableV2/hooks/useDataTableV2Pagination";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";

import { Add } from "@mui/icons-material";
import {
  downloadAutoInsurancePDFAsync,
  downloadCancellationReinstatementPDFAsync,
  downloadLiabilitySlipsPDFAsync,
  downloadOpcf48PDFAsync,
  downloadOpcf49PDFAsync,
  downloadPolicyIntactAuthorizationPDFAsync,
  downloadSef13DPDFAsync,
  downloadSef13HPDFAsync,
  downloadSef16PDFAsync,
  downloadSef17PDFAsync,
  downloadSef19PDFAsync,
  downloadSef40PDFAsync,
  previewAutoInsurancePDFAsync,
  previewCancellationReinstatementPDFAsync,
  previewLiabilitySlipsPDFAsync,
  previewOpcf48PDFAsync,
  previewOpcf49PDFAsync,
  previewPolicyIntactAuthorizationPDFAsync,
  previewSef13DPDFAsync,
  previewSef13HPDFAsync,
  previewSef16PDFAsync,
  previewSef17PDFAsync,
  previewSef19PDFAsync,
  previewSef40PDFAsync,
} from "../../../../../../redux/policyCommon/policyCommonActions";
import { COMMON_STATUS_WITH_ALL } from "../../../../../../constants/constants";
import { PageContainer } from "../../../../../../components/container/PageContainer";
import {
  downloadCommAutomobileSupplementaryFormPDFAsync,
  previewCommAutomobileSupplementaryFormPDFAsync,
} from "../../../../../../redux/commAutoPolicy/commAutoAllApplications/commAutoAllApplicationsActions";
import {
  downloadCommAutomobileCVSPDF,
  previewCommAutomobileCVSPDF,
} from "../../../../../../redux/commAutoPolicy/commAutoSupplement/commAutoSupplementActions";
import { CommAuto21BDialog } from "./CommAuto21BDialog/CommAuto21BDialog";
import { api } from "../../../../../../api/api";
import {
  generateExcelSheet,
  ICSVColumnsFormat,
} from "../../../../../../components/TableV2/preDefinedPlugins/DataTableV2CSVExport/DataTableV2CSVExport";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../../../../../redux/messages/messagesActions";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }: any) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const POLICY_FORM_TYPES = [
  { label: "All Forms", value: "all_forms" },
   { label: "OPCF-21B (Vehicle List)", value: "21B" },
  { label: "OPCF-48", value: "OPCF-48" },
  { label: "OPCF-49", value: "OPCF-49" },
  { label: "SEF-16", value: "SEF-16" },
  { label: "SEF-17", value: "SEF-17" },
  { label: "SEF-19", value: "SEF-19" },
  { label: "SEF-13d", value: "SEF-13d" },
  { label: "SEF-13h", value: "SEF-13h" },
  { label: "SEF-40", value: "SEF-40" },
  { label: "SEF-28A", value: "SEF-28A" },
  { label: "Liability Slips", value: "liability_slips" },
  {
    label: "Certificate of Liability Insurance",
    value: "certificate_of_liability_insurance",
  },
  {
    label: "Certificate of Automobile Insurance",
    value: "certificate_of_automobile_insurance",
  },
  {
    label: "Automobile Loss Notice",
    value: "automobile_loss_notice",
  },
  { label: "OPCF-16", value: "OPCF-16" },
  { label: "OPCF-17", value: "OPCF-17" },
  { label: "OPCF-28A", value: "OPCF-28A" },
  { label: "Payment Authorization Form", value: "payment_authorization" },
  { label: " Payment Authorization-Intact", value: "intact_authorization" },
  {
    label: "Cancellation or Reinstatement",
    value: "cancellation_or_reinstatement",
  },
  { label: "Privacy Consent", value: "privacy_consent" },
  { label: "Retiree Discount", value: "retiree_discount" },
  { label: "Ride Sharing", value: "ride_sharing" },
  { label: "Garage Supplement", value: "garage_supplementary" },
  {
    label: "Commercial Vehicle Supplement",
    value: "commercial_vehicle_supplement",
  },
 
];

const DOWNLOAD_FOR = [
  "liability_slips",
  "certificate_of_liability_insurance",
  "certificate_of_automobile_insurance",
  "OPCF-16",
  "OPCF-17",
  "OPCF-28A",
  "OPCF-48",
  "OPCF-49",
  "SEF-16",
  "SEF-17",
  "SEF-19",
  "SEF-13d",
  "SEF-13h",
  "SEF-40",
  "SEF-28A",
  "payment_authorization",
  "intact_authorization",
  "cancellation_or_reinstatement",
  "privacy_consent",
  "retiree_discount",
  "ride_sharing",
  "garage_supplementary",
  "commercial_vehicle_supplement",
];

const PREVIEW_FOR = [
  "liability_slips",
  "certificate_of_liability_insurance",
  "certificate_of_automobile_insurance",
  "OPCF-16",
  "OPCF-17",
  "OPCF-28A",
  "OPCF-48",
  "OPCF-49",
  "SEF-16",
  "SEF-17",
  "SEF-19",
  "SEF-13d",
  "SEF-13h",
  "SEF-40",
  "SEF-28A",
  "payment_authorization",
  "intact_authorization",
  "cancellation_or_reinstatement",
  "privacy_consent",
  "retiree_discount",
  "ride_sharing",
  "garage_supplementary",
  "commercial_vehicle_supplement",
];

export const CommAutoPolicyForms: React.FC = () => {
  const data = useOutletContext() as any;
  const customerId = data[0];
  const customerPolicyId = data[1];
  const [downloading, setDownloading] = React.useState(false);

  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.policyForms.policyFormsList
  );
  const { customerInfo } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.basicDetails
  );

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "-1"
  );
  const { tablePagination, onPageChange, onRowsPerPageChange } =
    useDataTableV2Pagination({
      pageNumber: 1,
      rowsInPerPage: 25,
      extraFetchFactor: 1,
    });
  const [policyFormRoot, setPolicyFormRoot] = React.useState("all_forms");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openPolicyCancel, setOpenPolicyCancel] =
    React.useState<boolean>(false);
  const [open21BDialog, setOpen21BDialog] = React.useState<boolean>(false);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFormTypeClick =
    (value: string, incremental_number?: any) => () => {
      setAnchorEl(null);
      let additionalparms = "";
      if (incremental_number) {
        additionalparms = "/" + incremental_number;
      }

      switch (value) {
        case "liability_slips": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-slips${additionalparms}`
          );

          return;
        }
        case "cancellation_or_reinstatement": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/cancellation-or-reinstatement${additionalparms}`
          );

          return;
        }
        case "certificate_of_liability_insurance": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-insurance${additionalparms}`
          );
          return;
        }
        case "certificate_of_automobile_insurance": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/certificate-of-insurance${additionalparms}`
          );
          return;
        }
        case "automobile_loss_notice": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/loss-notice${additionalparms}`
          );

          return;
        }
        case "OPCF-16": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-16${additionalparms}`
          );

          return;
        }
        case "OPCF-17": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-17${additionalparms}`
          );

          return;
        }
        case "OPCF-48": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-48${additionalparms}`
          );

          return;
        }
        case "OPCF-49": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-49${additionalparms}`
          );

          return;
        }
        case "SEF-16": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-16${additionalparms}`
          );

          return;
        }
        case "SEF-17": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-17${additionalparms}`
          );

          return;
        }
        case "SEF-19": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-19${additionalparms}`
          );

          return;
        }
        case "SEF-13d": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13d${additionalparms}`
          );

          return;
        }
        case "SEF-13h": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13h${additionalparms}`
          );

          return;
        }
        case "SEF-40": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-40${additionalparms}`
          );

          return;
        }
        case "SEF-28A": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-28A${additionalparms}`
          );

          return;
        }
        case "OPCF-28A": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-28A${additionalparms}`
          );

          return;
        }
        case "payment_authorization": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization${additionalparms}`
          );

          return;
        }
        case "privacy_consent": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/consent-form${additionalparms}`
          );

          return;
        }
        case "retiree_discount": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/retiree-discount${additionalparms}`
          );

          return;
        }
        case "ride_sharing": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sharing-fillable${additionalparms}`
          );

          return;
        }
       
        case "intact_authorization": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/intact-authorization${additionalparms}`
          );
          return;
        }
        case "garage_supplementary": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/garage-supplementary${additionalparms}`
          );

          return;
        }
        case "commercial_vehicle_supplement": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/commercial-vehicle-supplement${additionalparms}`
          );

          return;
        }
        case "21B": {
          setOpen21BDialog(true);
          return;
        }
      }
    };

  const handleDuplicateFormTypeClick =
    (value: string, incremental_number?: any) => () => {
      setAnchorEl(null);
      let additionalparms = "";
      if (incremental_number) {
        additionalparms = "/" + incremental_number;
      }

      switch (value) {
        case "liability_slips": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-slips/duplicate${additionalparms}`
          );

          return;
        }
        case "cancellation_or_reinstatement": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/cancellation-or-reinstatement/duplicate${additionalparms}`
          );

          return;
        }
        case "certificate_of_liability_insurance": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-insurance/duplicate${additionalparms}`
          );
          return;
        }
        case "certificate_of_automobile_insurance": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/certificate-of-insurance/duplicate${additionalparms}`
          );
          return;
        }
        case "automobile_loss_notice": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/automobile_loss_notice/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-16": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-16/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-17": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-17/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-48": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-48/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-49": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-49/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-16": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-16/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-17": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-17/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-19": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-19/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-13d": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13d/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-13h": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13h/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-40": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-40/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-28A": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-28A/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-28A": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-28A/duplicate${additionalparms}`
          );

          return;
        }
        case "payment_authorization": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization/duplicate${additionalparms}`
          );

          return;
        }
        case "privacy_consent": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/consent-form/duplicate${additionalparms}`
          );

          return;
        }
        case "retiree_discount": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/retiree-discount/duplicate${additionalparms}`
          );

          return;
        }
        case "ride_sharing": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sharing-fillable/duplicate${additionalparms}`
          );

          return;
        }
        case "garage_supplementary": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/garage-supplementary/duplicate${additionalparms}`
          );

          return;
        }
        case "commercial_vehicle_supplement": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/commercial-vehicle-supplement/duplicate${additionalparms}`
          );
          return;
        }

        case "intact_authorization": {
          navigate(
            `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/intact-authorization/duplicate${additionalparms}`
          );

          return;
        }
      }
    };

  const handleDownloadClick = (
    policyFormType: string,
    incremental_number: number
  ) => {
    switch (policyFormType) {
      case "liability_slips": {
        dispatch(
          downloadLiabilitySlipsPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "certificate_of_liability_insurance": {
        dispatch(
          downloadCommAutomobileLiabilityInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-16": {
        dispatch(
          downloadCommAutomobileOcep16PDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-17": {
        dispatch(
          downloadCommAutomobileOcep17PDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-48": {
        dispatch(
          downloadOpcf48PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-49": {
        dispatch(
          downloadOpcf49PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-16": {
        dispatch(
          downloadSef16PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-17": {
        dispatch(
          downloadSef17PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-19": {
        dispatch(
          downloadSef19PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }

      case "SEF-13d": {
        dispatch(
          downloadSef13DPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-13h": {
        dispatch(
          downloadSef13HPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-40": {
        dispatch(
          downloadSef40PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-28A": {
        dispatch(
          downloadCommAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "sef",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "certificate_of_automobile_insurance": {
        dispatch(
          downloadAutoInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }

      case "OPCF-28A": {
        dispatch(
          downloadCommAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "opcf",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "payment_authorization": {
        dispatch(
          downloadCommAutomobileAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "privacy_consent": {
        dispatch(
          downloadCommAutomobileConsentFormPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "retiree_discount": {
        dispatch(
          downloadCommAutomobileRetireeDiscountPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "ride_sharing": {
        dispatch(
          downloadCommAutomobileSharingFillablePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      // case "garage_supplementary": {
      //   dispatch(
      //     downloadAutomobileSharingFillablePDFAsync(
      //       customerPolicyId,
      //       incremental_number,
      //       () => {
      //         setDownloading(false);
      //       }
      //     )
      //   );
      //   return;
      // }
      case "cancellation_or_reinstatement": {
        dispatch(
          downloadCancellationReinstatementPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "intact_authorization": {
        dispatch(
          downloadPolicyIntactAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "garage_supplementary": {
        dispatch(
          downloadCommAutomobileSupplementaryFormPDFAsync(
            customerPolicyId,
            incremental_number
          )
        );
        return;
      }
      case "commercial_vehicle_supplement": {
        dispatch(
          downloadCommAutomobileCVSPDF(customerPolicyId, incremental_number)
        );
        return;
      }
    }
  };

  const handlePreviewClick = (
    policyFormType: string,
    incremental_number: number
  ) => {
    switch (policyFormType) {
      case "liability_slips": {
        dispatch(
          previewLiabilitySlipsPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "certificate_of_liability_insurance": {
        dispatch(
          previewCommAutomobileLiabilityInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-16": {
        dispatch(
          previewCommAutomobileOcep16PDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-17": {
        dispatch(
          previewCommAutomobileOcep17PDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-48": {
        dispatch(
          previewOpcf48PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "OPCF-49": {
        dispatch(
          previewOpcf49PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-16": {
        dispatch(
          previewSef16PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-17": {
        dispatch(
          previewSef17PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-19": {
        dispatch(
          previewSef19PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-13d": {
        dispatch(
          previewSef13DPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-13h": {
        dispatch(
          previewSef13HPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-40": {
        dispatch(
          previewSef40PDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "SEF-28A": {
        dispatch(
          previewCommAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "sef",
            () => {}
          )
        );
        return;
      }
      case "certificate_of_automobile_insurance": {
        dispatch(
          previewAutoInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "OPCF-28A": {
        dispatch(
          previewCommAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "opcf",
            () => {}
          )
        );
        return;
      }
      case "payment_authorization": {
        dispatch(
          previewCommAutomobileAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "privacy_consent": {
        dispatch(
          previewCommAutomobileConsentFormPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "retiree_discount": {
        dispatch(
          previewCommAutomobileRetireeDiscountPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "ride_sharing": {
        dispatch(
          previewCommAutomobileSharingFillablePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      // case "garage_supplementary": {
      //   dispatch(
      //     previewAutomobileSupplementaryFormPDFAsync(
      //       customerPolicyId,
      //       incremental_number,
      //       () => {}
      //     )
      //   );
      //   return;
      // }
      case "cancellation_or_reinstatement": {
        dispatch(
          previewCancellationReinstatementPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "intact_authorization": {
        dispatch(
          previewPolicyIntactAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            "commauto",
            () => {}
          )
        );
        return;
      }
      case "garage_supplementary": {
        dispatch(
          previewCommAutomobileSupplementaryFormPDFAsync(
            customerPolicyId,
            incremental_number
          )
        );
        return;
      }
      case "commercial_vehicle_supplement": {
        dispatch(
          previewCommAutomobileCVSPDF(customerPolicyId, incremental_number)
        );
        return;
      }
    }
  };

  const handle21BDownloadClick = async () => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-commauto-twenty-one-b-form?customer_policy_id=${customerPolicyId}`
      );
      const data = res.data.data || [];

      const columns: ICSVColumnsFormat[] = [
        { columnName: "Vehicle Number", fieldName: "incremental_number", width: 15 },
        { columnName: "Unit Number", fieldName: "unit_number", width: 15 },
        { columnName: "Year", fieldName: "year", width: 10 },
        { columnName: "Make", fieldName: "make", width: 15 },
        { columnName: "Model", fieldName: "model", width: 15 },
        { columnName: "VIN", fieldName: "vin", width: 20 },
        { columnName: "Gross Vehicle Weight", fieldName: "gross_vehicle_weight", width: 20 },
        { columnName: "Vehicle Type", fieldName: "vehicle_type", width: 15 },
        { columnName: "Body Type", fieldName: "body_type", width: 15 },
        { columnName: "Actual Cash Value", fieldName: "acutal_cash_value", width: 18 },
        { columnName: "List Price New", fieldName: "list_price_new", width: 15 },
        { columnName: "Owned", fieldName: "owned", width: 10 },
        { columnName: "Leased", fieldName: "leased", width: 10 },
        { columnName: "Ownership of Vehicle", fieldName: "ownership", width: 20 },
        { columnName: "Owner Operator of Lessee", fieldName: "owner_operator_or_lessee", width: 25 },
        { columnName: "Plate Number", fieldName: "plate_number", width: 15 },
        { columnName: "Vehicle Registration Province", fieldName: "vehicle_registration_province", width: 25 },
        { columnName: "Interested Party", fieldName: "name", width: 20 },
        { columnName: "Address", fieldName: "address", width: 25 },
        { columnName: "Description", fieldName: "reason", width: 20 },
        { columnName: "Effective Date", fieldName: "create_ts", width: 18 },
        { columnName: "Status (Active/Deleted)", fieldName: "status", width: 20 },
      ];

      await generateExcelSheet(data, columns, "CommAuto-21B-Form");
    } catch (err: any) {
      console.error(err);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

  const {
    state: { dateState, searchState, tabs, columnsConfig, dense },
    setDateState,
    setSearchState,
    setSelectedTab,
    setColumnVisibility,

    onDenseToggle,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last90Days",
      selectedTab: "-1",
    },
  });

  const fetchList = () => {
    dispatch(
      fetchCommAutoPolicyFormsListAsync(
        customerPolicyId,
        policyFormRoot,
        tablePagination.pageNumber,
        tablePagination.rowsInPerPage,
        status
      )
    );
  };

  const commAutoPolicyFormDataProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    extraFetchFactor: tablePagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "none",
    uniqueRowKeyName: "incemental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "policy_form_types",
            onRender: () => (
              <ControlledCustomSelect
                sx={{ minWidth: "120px" }}
                value={policyFormRoot}
                options={POLICY_FORM_TYPES}
                onChange={(e) => setPolicyFormRoot(e.target.value as string)}
              ></ControlledCustomSelect>
            ),
          },
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS_WITH_ALL}
              />
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutoPolicyForm) => {
          return (
            <StandardTableActions
              downloadLoading={downloading}
              onEditClick={row.policy_form_type !== "OPCF-21B" ? handleFormTypeClick(
                row.policy_form_type,
                row.incremental_number
              ) : undefined}
              onDuplicateClick={row.policy_form_type !== "OPCF-21B" ? handleDuplicateFormTypeClick(
                row.policy_form_type,
                row.incremental_number
              ) : undefined}
              onDownLoadClick={
                row.policy_form_type === "OPCF-21B"
                  ? handle21BDownloadClick
                  : row.status?.toLowerCase() === "active" &&
                    DOWNLOAD_FOR.includes(row.policy_form_type)
                  ? () =>
                      handleDownloadClick(
                        row.policy_form_type,
                        row.incremental_number as any
                      )
                  : undefined
              }
              onDownloadPreview={
                row.status?.toLowerCase() === "active" &&
                PREVIEW_FOR.includes(row.policy_form_type)
                  ? () =>
                      handlePreviewClick(
                        row.policy_form_type,
                        row.incremental_number as any
                      )
                  : undefined
              }
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutoPolicyForm) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "type",
        headerName: "Policy Form Type",
        fieldName: "policy_form_type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 30,
        onRowCellValueRender: (value, row) => {
          return formatText(value);
        },
      },
      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
    ],
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "create_ts",
      },
    },
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
    dense: dense,
    onDenseToggle: onDenseToggle,
  };

  React.useEffect(() => {
    if (customerPolicyId) {
      fetchList();
    }
  }, [customerPolicyId, tablePagination, policyFormRoot, status]);

  return (
    <PageContainer title={"Policies"} description={"Policies"}>
      <PageLoader
        loading={customerInfo.loading === LoadState.InProgress}
        error={customerInfo.error ? { message: customerInfo.error } : null}
      >
        <PageTitleBar
          heading="Policy Forms"
          rightHeading={
            <Stack>
              <Button
                id="demo-customized-button"
                aria-controls={open ? "demo-customized-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="contained"
                disableElevation
                onClick={handleClick}
                startIcon={<Add />}
                endIcon={<KeyboardArrowDownIcon />}
              >
                Create
              </Button>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
              >
                {POLICY_FORM_TYPES.slice(1).map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={handleFormTypeClick(item.value)}
                      disableRipple
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </StyledMenu>
            </Stack>
          }
        />

        <DataTableV2 {...commAutoPolicyFormDataProps} />
        {/* {openPolicyCancel && customerId && customerPolicyId && (
          <CancellationDialog
            customerPolicyId={customerPolicyId}
            open={openPolicyCancel}
            data={customerInfo}
            onClose={() => setOpenPolicyCancel(false)}
            changeNextStep={handlePolicyCancelNextStep}
          />
        )} */}

        {open21BDialog && (
          <CommAuto21BDialog
            open={open21BDialog}
            onClose={() => setOpen21BDialog(false)}
            customerPolicyId={customerPolicyId}
            onSuccess={fetchList}
          />
        )}
      </PageLoader>
    </PageContainer>
  );
};
